<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import RadioCard from 'dashboard/components-next/radioCard/RadioCard.vue';
import FairDistribution from 'dashboard/components-next/AssignmentPolicy/components/FairDistribution.vue';
import DurationInput from 'dashboard/components-next/input/DurationInput.vue';
import { DURATION_UNITS } from 'dashboard/components-next/input/constants';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  ROUND_ROBIN,
  BALANCED,
  EARLIEST_CREATED,
  LONGEST_WAITING,
  DEFAULT_FAIR_DISTRIBUTION_LIMIT,
  DEFAULT_FAIR_DISTRIBUTION_WINDOW,
  DEFAULT_EXCLUDE_OLDER_THAN_HOURS,
} from 'dashboard/routes/dashboard/settings/assignmentPolicy/constants';

const props = defineProps({
  initialData: { type: Object, default: () => ({}) },
  isLoading: { type: Boolean, default: false },
});
const emit = defineEmits(['submit']);
const { t } = useI18n();
const MIN_EXCLUSION_MINUTES = 60;
const MAX_EXCLUSION_MINUTES = 1438560;
const exclusionUnit = ref(DURATION_UNITS.DAYS);
const state = reactive({
  assignmentOrder: ROUND_ROBIN,
  conversationPriority: EARLIEST_CREATED,
  fairDistributionLimit: DEFAULT_FAIR_DISTRIBUTION_LIMIT,
  fairDistributionWindow: DEFAULT_FAIR_DISTRIBUTION_WINDOW,
  excludeOlderThanHours: DEFAULT_EXCLUDE_OLDER_THAN_HOURS,
});

const excludeOlderThanMinutes = computed({
  get: () =>
    state.excludeOlderThanHours == null
      ? null
      : state.excludeOlderThanHours * 60,
  set: minutes => {
    state.excludeOlderThanHours =
      minutes == null ? null : Math.round(minutes / 60);
  },
});

const assignmentOrderOptions = computed(() => [
  {
    key: ROUND_ROBIN,
    label: t(
      'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.FORM.ASSIGNMENT_ORDER.ROUND_ROBIN.LABEL'
    ),
    description: t(
      'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.FORM.ASSIGNMENT_ORDER.ROUND_ROBIN.DESCRIPTION'
    ),
    isActive: state.assignmentOrder === ROUND_ROBIN,
  },
  {
    key: BALANCED,
    label: t(
      'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.FORM.ASSIGNMENT_ORDER.BALANCED.LABEL'
    ),
    description: t(
      'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.FORM.ASSIGNMENT_ORDER.BALANCED.DESCRIPTION'
    ),
    isActive: state.assignmentOrder === BALANCED,
  },
]);

const assignmentPriorityOptions = computed(() => [
  {
    key: EARLIEST_CREATED,
    label: t(
      'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.FORM.ASSIGNMENT_PRIORITY.EARLIEST_CREATED.LABEL'
    ),
    description: t(
      'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.FORM.ASSIGNMENT_PRIORITY.EARLIEST_CREATED.DESCRIPTION'
    ),
    isActive: state.conversationPriority === EARLIEST_CREATED,
  },
  {
    key: LONGEST_WAITING,
    label: t(
      'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.FORM.ASSIGNMENT_PRIORITY.LONGEST_WAITING.LABEL'
    ),
    description: t(
      'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.FORM.ASSIGNMENT_PRIORITY.LONGEST_WAITING.DESCRIPTION'
    ),
    isActive: state.conversationPriority === LONGEST_WAITING,
  },
]);

const radioSections = computed(() => [
  {
    key: 'assignmentOrder',
    label: t(
      'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.FORM.ASSIGNMENT_ORDER.LABEL'
    ),
    options: assignmentOrderOptions.value,
  },
  {
    key: 'conversationPriority',
    label: t(
      'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.FORM.ASSIGNMENT_PRIORITY.LABEL'
    ),
    options: assignmentPriorityOptions.value,
  },
]);

watch(
  () => props.initialData,
  value => {
    Object.assign(state, value);
    exclusionUnit.value =
      value.excludeOlderThanHours && value.excludeOlderThanHours % 24 !== 0
        ? DURATION_UNITS.HOURS
        : DURATION_UNITS.DAYS;
  },
  { immediate: true, deep: true }
);

const handleSubmit = () => emit('submit', { ...state });
</script>

<template>
  <form class="pt-2" @submit.prevent="handleSubmit">
    <div class="flex flex-col gap-4 divide-y divide-n-weak mb-4">
      <div class="flex flex-col items-center">
        <div
          v-for="section in radioSections"
          :key="section.key"
          class="py-4 flex flex-col items-start gap-3 w-full"
        >
          <WithLabel
            :label="section.label"
            name="assignmentPolicy"
            class="w-full flex items-start flex-col gap-3"
          >
            <div class="grid grid-cols-1 xs:grid-cols-2 gap-4 w-full">
              <RadioCard
                v-for="option in section.options"
                :id="option.key"
                :key="option.key"
                :label="option.label"
                :description="option.description"
                :is-active="option.isActive"
                @select="state[section.key] = $event"
              />
            </div>
          </WithLabel>
        </div>
      </div>

      <div class="pt-4 pb-2 flex-col flex gap-4">
        <div class="flex flex-col items-start gap-1 py-1">
          <label class="text-sm font-medium text-n-slate-12 py-1">{{
            t(
              'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.FORM.FAIR_DISTRIBUTION.LABEL'
            )
          }}</label>
          <p class="mb-0 text-n-slate-11 text-sm">
            {{
              t(
                'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.FORM.FAIR_DISTRIBUTION.DESCRIPTION'
              )
            }}
          </p>
        </div>
        <FairDistribution
          v-model:fair-distribution-limit="state.fairDistributionLimit"
          v-model:fair-distribution-window="state.fairDistributionWindow"
        />
      </div>

      <div class="pt-4 pb-2 flex-col flex gap-4">
        <div class="flex flex-col items-start gap-1 py-1">
          <label class="text-sm font-medium text-n-slate-12 py-1">{{
            t(
              'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.FORM.EXCLUDE_OLDER_THAN.LABEL'
            )
          }}</label>
          <p class="mb-0 text-n-slate-11 text-sm">
            {{
              t(
                'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.FORM.EXCLUDE_OLDER_THAN.DESCRIPTION'
              )
            }}
          </p>
        </div>
        <DurationInput
          v-model:unit="exclusionUnit"
          v-model:model-value="excludeOlderThanMinutes"
          :min="MIN_EXCLUSION_MINUTES"
          :max="MAX_EXCLUSION_MINUTES"
        />
      </div>
    </div>
    <Button
      type="submit"
      :label="$t('INBOX_MGMT.ASSIGNMENT_POLICY.SAVE')"
      :disabled="isLoading"
      :is-loading="isLoading"
    />
  </form>
</template>
